import { useEffect, useState } from 'react';
import type { DealValues, RegistryHouseDefaults } from '../../api/registryDealTypes';
import { errorMessage, type RegistryApi } from '../../api/registryApi';
import { sectionErrors } from '../../domain/setup/setupRules';
import { type RegistrySession, useRegistrySession } from '../../session/RegistrySession';
import { useToast } from '../../ui/feedback';
import { isNotSet, LIVE_DEFAULT_SECTIONS, sectionKeys } from './liveDefaultSections';

/** The setup's own checks (formats and cross-field rules) on entered values: a blank default is fine. */
export function defaultsErrors(values: DealValues): Record<string, string> {
  return Object.fromEntries(
    LIVE_DEFAULT_SECTIONS.flatMap((section) => {
      const keys = new Set(sectionKeys(section));
      return Object.entries(sectionErrors(section.id, values)).filter(
        ([key]) => keys.has(key) && !isNotSet(values[key]),
      );
    }),
  );
}

/**
 * Uploads the default logo, makes sure Default Values says a logo is uploaded, then reads the
 * defaults back the way the page loads them. The upload's own response leaves out the built-in
 * starting values, so using it as the saved copy made a second upload show every built-in value
 * as an unsaved change (finding 40).
 */
export async function uploadDefaultLogo(api: RegistryApi, file: File) {
  const updated = await api.uploadLogo(file);
  if (updated.values['brand.logo'] !== 'UPLOADED') {
    await api.updateDefaults({ 'brand.logo': 'UPLOADED' });
  }
  return api.getDefaults();
}

/** The saved defaults and the page's working copy, loaded once from the server. */
function useLoadedDefaults(session: RegistrySession) {
  const [saved, setSaved] = useState<RegistryHouseDefaults | null>(null);
  const [draft, setDraft] = useState<DealValues>({});
  const [loadError, setLoadError] = useState('');
  useEffect(() => {
    session.api
      .getDefaults()
      .then((defaults) => {
        setSaved(defaults);
        setDraft(defaults.values);
      })
      .catch((failure) => setLoadError(errorMessage(failure)));
  }, [session]);
  return { saved, setSaved, draft, setDraft, loadError };
}

export function useHouseDefaults() {
  const session = useRegistrySession()!;
  const toast = useToast();
  const { saved, setSaved, draft, setDraft, loadError } = useLoadedDefaults(session);
  const [saveError, setSaveError] = useState('');
  const [logoError, setLogoError] = useState('');
  const [busy, setBusy] = useState(false);
  const changed = Object.keys(draft).filter((key) => draft[key] !== saved?.values[key]);
  const errors = defaultsErrors(draft);
  const update = (key: string, value: DealValues[string]) =>
    setDraft((current) => ({ ...current, [key]: value }));
  const discard = () => {
    setDraft(saved?.values ?? {});
    setSaveError('');
  };
  const save = async () => {
    setBusy(true);
    setSaveError('');
    try {
      const updated = await session.api.updateDefaults(
        Object.fromEntries(changed.map((key) => [key, draft[key]])),
      );
      setSaved(updated);
      setDraft(updated.values);
      toast('Defaults saved');
    } catch (failure) {
      const message = errorMessage(failure);
      setSaveError(message);
      toast(`Defaults not saved: ${message}`, 'error');
    } finally {
      setBusy(false);
    }
  };
  const uploadLogo = async (file: File) => {
    setBusy(true);
    setLogoError('');
    try {
      const updated = await uploadDefaultLogo(session.api, file);
      setSaved(updated);
      setDraft((current) => ({ ...updated.values, ...current, 'brand.logo': 'UPLOADED' }));
      toast('Logo uploaded');
    } catch (failure) {
      const message = errorMessage(failure);
      setLogoError(message);
      toast(`Logo not uploaded: ${message}`, 'error');
    } finally {
      setBusy(false);
    }
  };
  return {
    session,
    saved,
    draft,
    update,
    loadError,
    saveError,
    logoError,
    busy,
    changed,
    errors,
    save,
    discard,
    uploadLogo,
  };
}
